import { Box, Button, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { FC, useRef, useState } from 'react';

import {
  AbgTypography,
  BackIcon,
  CalendarIcon,
  DateRangePicker,
} from '@abg/kit-base';

import { useDateTimePickerController } from './hooks/use-date-time-picker-controller';
import { DateTimePickerErrorBanner } from './molecules/DateTimePickerErrorBanner';
import { DateTimePickerFooter } from './molecules/DateTimePickerFooter';
import { PickerAccordion } from './molecules/PickerAccordion';
import {
  accordionsContainerStyles,
  backIconStyles,
  dateTimeDrawerStyles,
  drawerIconContainerStyles,
  locationTimeOptionStyles,
} from './DateTimePickerMobile.styling';
import {
  DateTimeAccordions,
  DateTimePickerErrorEnum,
  DateTimePickerProperties,
  HandleCancel,
  InitialDraftStateT,
} from './types';
import { formatDatesText } from './utils';

export const DateTimePickerMobile: FC<DateTimePickerProperties> = ({
  isOpen,
  onClose,
  onDone,
  uiTextData,
  timePickerOptions,
  pickUpOpeningHours = [],
  pickupHolidays = [],
  dropoffOpeningHours = [],
  dropoffHolidays = [],
  dropoff24hrsFlag,
  dateRangePickerProps,
  initialDraftState,
  maxBookingPeriod,
  brand,
  weekStartsOn,
  locale,
  timeFormat,
  disabledRange,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const {
    draftValues,
    nonWorkingPickupHolidays,
    nonWorkingDropoffHolidays,
    todayAsUnavailableDay,
    pickupSubtitle,
    dropoffSubtitle,
    pickupLocationTimeOptions,
    dropoffLocationTimeOptions,
    handleValueChange,
    handleCancel,
    handleDone,
    pickUpLocationClosedDays,
    dropoffLocationClosedDays,
    daysSelected,
    showDateTimePickerError,
  } = useDateTimePickerController({
    onClose,
    onDone,
    uiTextData,
    timePickerOptions,
    pickUpOpeningHours,
    pickupHolidays,
    dropoffOpeningHours,
    dropoffHolidays,
    dropoff24hrsFlag,
    initialDraftState,
    timeFormat,
    maxBookingPeriod,
  });

  const [expanded, setExpanded] = useState<string | false>(
    DateTimeAccordions.dates,
  );

  const toggleAccordion =
    (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false);
    };

  const { datesTitle, timePickerPickupTitle, timePickerDropoffTitle } =
    uiTextData;

  const isDropoffTimeUnreachable = dropoffLocationTimeOptions.length === 0;

  const handleClickCancel: HandleCancel = (event_) => {
    handleCancel(event_);
    setExpanded(DateTimeAccordions.dates);
  };

  const handleClickDone = () => {
    handleDone();
    setExpanded(DateTimeAccordions.dates);
  };

  const datePickerContainerReference = useRef<HTMLDivElement>(null);

  return (
    <Drawer
      open={isOpen}
      onClose={handleClickCancel}
      anchor="right"
      slotProps={{
        root: {
          sx: {
            zIndex: 1400,
          },
        },
        paper: {
          sx: dateTimeDrawerStyles,
        },
      }}
    >
      <Box sx={drawerIconContainerStyles}>
        <Button
          aria-label="Go back"
          onClick={handleClickCancel}
          variant="text"
          sx={backIconStyles}
        >
          <BackIcon />
        </Button>
      </Box>

      <Box sx={accordionsContainerStyles}>
        <PickerAccordion
          isExpanded={expanded === DateTimeAccordions.dates}
          toggleAccordion={toggleAccordion(DateTimeAccordions.dates)}
          label={formatDatesText(draftValues.dates, locale)}
          title={datesTitle}
          IconComponent={CalendarIcon}
          ref={datePickerContainerReference}
          showDateTimePickerError={showDateTimePickerError}
          maxBookingPeriod={maxBookingPeriod}
        >
          <DateRangePicker
            value={draftValues.dates}
            weekStartsOn={weekStartsOn}
            numberOfMonths={isTablet ? 8 : 3}
            locale={locale}
            minDateValue={dateRangePickerProps.dateRangePickerMinDateValue}
            maxDateValue={dateRangePickerProps.dateRangePickerMaxDateValue}
            disabledRange={disabledRange}
            maxRentalDays={maxBookingPeriod}
            maxRentalStartDate={draftValues.dates?.from}
            onChange={(dates: InitialDraftStateT['dates']) =>
              handleValueChange('dates', dates)
            }
            pickUpLocationHolidays={[
              todayAsUnavailableDay,
              ...nonWorkingPickupHolidays,
            ]}
            pickUpLocationClosedDays={pickUpLocationClosedDays}
            dropOffLocationHolidays={
              dropoff24hrsFlag ? [] : nonWorkingDropoffHolidays
            }
            dropOffLocationClosedDays={
              dropoff24hrsFlag ? [] : dropoffLocationClosedDays
            }
            containerRef={datePickerContainerReference}
            hideNavigation
            isScrollable
            allowPickupReselection
          />
        </PickerAccordion>

        <PickerAccordion
          isExpanded={expanded === DateTimeAccordions.pickupTime}
          toggleAccordion={toggleAccordion(DateTimeAccordions.pickupTime)}
          label={
            pickupLocationTimeOptions.find(
              (time) => time.value === draftValues.pickupTime,
            )?.label
          }
          title={timePickerPickupTitle}
          subtitle={pickupSubtitle}
          showDateTimePickerError={showDateTimePickerError}
          maxBookingPeriod={maxBookingPeriod}
        >
          {pickupLocationTimeOptions.map((time) => {
            const isSelected = time.value === draftValues.pickupTime;
            return (
              <Box
                aria-checked={isSelected}
                role="radio"
                key={time.value}
                sx={locationTimeOptionStyles}
                component={'button'}
                onClick={() => handleValueChange('pickupTime', time.value)}
              >
                <AbgTypography
                  variant={
                    isSelected ? 'bodyRegularBold' : 'bodyRegularRegular'
                  }
                >
                  {time.label}
                </AbgTypography>
              </Box>
            );
          })}
        </PickerAccordion>

        <PickerAccordion
          isExpanded={expanded === DateTimeAccordions.dropoffTime}
          toggleAccordion={toggleAccordion(DateTimeAccordions.dropoffTime)}
          label={
            dropoffLocationTimeOptions.find(
              (time) => time.value === draftValues.dropoffTime,
            )?.label
          }
          title={timePickerDropoffTitle}
          subtitle={dropoffSubtitle}
          showDateTimePickerError={showDateTimePickerError}
          maxBookingPeriod={maxBookingPeriod}
        >
          {dropoffLocationTimeOptions.map((time) => {
            const isSelected = time.value === draftValues.dropoffTime;
            return (
              <Box
                aria-checked={isSelected}
                role="radio"
                key={time.value}
                sx={locationTimeOptionStyles}
                component={'button'}
                onClick={() => handleValueChange('dropoffTime', time.value)}
              >
                <AbgTypography
                  variant={
                    isSelected ? 'bodyRegularBold' : 'bodyRegularRegular'
                  }
                >
                  {time.label}
                </AbgTypography>
              </Box>
            );
          })}
        </PickerAccordion>
      </Box>

      {!expanded && showDateTimePickerError && (
        <DateTimePickerErrorBanner
          showDateTimePickerError={
            showDateTimePickerError as DateTimePickerErrorEnum
          }
          maxBookingPeriod={maxBookingPeriod}
        />
      )}

      <DateTimePickerFooter
        brand={brand}
        isDropoffTimeUnreachable={isDropoffTimeUnreachable}
        uiTextData={uiTextData}
        daysSelected={daysSelected}
        showDateTimePickerError={showDateTimePickerError}
        onClose={handleClickCancel}
        onDone={handleClickDone}
      />
    </Drawer>
  );
};
